import React, { useEffect, useState, useRef } from "react";
import {
	Button,
	Col,
	Nav,
	NavItem,
	NavLink,
	TabContent,
	TabPane,
} from "reactstrap";
import organizationalStructureService from "services/OrganizationalStructureService";
import { withTranslation } from "react-i18next";
import permissionsService from "services/permissionsService";
import Loading from "components/Common/Loading";
import classnames from "classnames";
import MaterialTableContainerWithNoFilters from "components/Common/MaterialTableContainerWithNoFilters";
import {
	getTableBodyCellProps,
	getTableHeaderCellProps,
} from "components/Common/MaterialTableStyles";

import OrgChartComponent from "./components/OrgChartComponent";
import OrgChartSimulation from "./components/OrgChartSimulation";
import {
	Plus,
	FilePdf,
	FileDoc,
	Pencil,
	FloppyDisk,
} from "@phosphor-icons/react";
import ModalCreateEditItem from "./ModalCreateEditItem";
import userService from "services/UserService";
import Select from "react-select";
import ataEncaminhamentosService from "services/AtaEncaminhamentosService";
import indicadoresService from "services/IndicadoresService";
import actionPlansService from "services/ActionPlansService";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import organizationalStructureSimulationService from "services/OrganizationalStructureSimulationService";

const EstruturaOrgnizacional = (props) => {
	document.title = "Senda | Estrutura organizacional";

	const customStyles = {
		option: (provided, state) => ({
			...provided,
			color: state.isSelected ? "blue" : "black",
			backgroundColor: "white",
			"&:hover": {
				backgroundColor: "#556ee6",
			},
		}),
	};

	const [items, setItems] = useState([]);
	const [simulationItems, setSimulationItems] = useState([]);
	const [itemsKey, setItemsKey] = useState(JSON.stringify(items));
	const [temPermissaoVisualizar, setTemPermissaoVisualizar] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [openCreateEditModal, setOpenCreateEditModal] = useState(false);
	const [data, setData] = useState({});
	const [isSimulation, setIsSimulation] = useState(false);
	const [activeTab, setActiveTab] = useState("organograma");
	const [atasData, setAtasData] = useState([]);
	const [planosDeAcaoData, setPlanosDeAcaoData] = useState([]);
	const [indicadoresData, setIndicadoresData] = useState([]);
	const [departamentos, setDepartamentos] = useState([]);
	const [usuarios, setUsuarios] = useState([]);

	// Estados para os selects das atas
	const [atasDepartamento, setAtasDepartamento] = useState({});
	const [atasResponsavel, setAtasResponsavel] = useState({});

	// Estados para os selects dos planos de ação
	const [planosDeAcaoDepartamento, setPlanosDeAcaoDepartamento] = useState(
		{},
	);
	const [planosDeAcaoResponsavel, setPlanosDeAcaoResponsavel] = useState({});
	const [planosDeAcaoAprovador, setPlanosDeAcaoAprovador] = useState({});

	// Estados para os selects dos indicadores
	const [indicadoresDepartamento, setIndicadoresDepartamento] = useState({});

	const orgChartRef = useRef(null); // Referência para o OrgChartComponent
	const orgChartSimulationRef = useRef(null); // Referência para o OrgChartSimulation

	useEffect(() => {
		setItemsKey(JSON.stringify(items));
	}, [items]);

	// Função para lidar com o clique no botão "Exportar PDF"
	const handleExportPdf = () => {
		if (orgChartRef.current) {
			orgChartRef.current.downloadPdf();
		}
		if (orgChartSimulationRef.current) {
			orgChartSimulationRef.current.downloadPdf();
		}
	};

	// Função para lidar com o clique no botão "Exportar Word"
	const handleExportWord = () => {
		if (orgChartRef.current) {
			orgChartRef.current.downloadWord();
		}
		if (orgChartSimulationRef.current) {
			orgChartSimulationRef.current.downloadWord();
		}
	};

	const getAtasMigrations = async () => {
		try {
			const response =
				await organizationalStructureService.getAtasMigrations();

			const sortedData = response.sort((a, b) =>
				a.referrals.localeCompare(b.referrals, "pt-BR"),
			);

			setAtasData(sortedData);
		} catch (error) {
			console.log(error);
		}
	};

	const getPlanoAcaoMigrations = async () => {
		try {
			const response =
				await organizationalStructureService.getPlanoAcaoMigrations();

			const sortedData = response.sort((a, b) =>
				a.guideline.localeCompare(b.guideline, "pt-BR"),
			);

			setPlanosDeAcaoData(sortedData);
		} catch (error) {
			console.log(error);
		}
	};

	const getIndicadoresMigrations = async () => {
		try {
			const response =
				await organizationalStructureService.getIndicadoresMigrations();

			const sortedData = response.sort((a, b) =>
				a.intention_goals.localeCompare(b.intention_goals, "pt-BR"),
			);

			setIndicadoresData(sortedData);
		} catch (error) {
			console.log(error);
		}
	};

	const getUsuarios = async () => {
		try {
			const response = await userService.get();
			const options = response.map((item) => ({
				value: item.id,
				label: item.lastname,
			}));

			const sortedOptions = options.sort((a, b) =>
				a.label.localeCompare(b.label, "pt-BR"),
			);

			setUsuarios(sortedOptions);
		} catch (error) {
			console.log(error);
		}
	};

	const getDepartamentos = async () => {
		try {
			const response = await organizationalStructureService.getAll();

			const options = response.map((item) => ({
				value: item.id,
				label: item.name,
				active: item.active === "1",
				parent_id: item.parent_id,
			}));

			const sortedOptions = options.sort((a, b) =>
				a.label.localeCompare(b.label, "pt-BR"),
			);

			setDepartamentos(sortedOptions);
		} catch (error) {
			console.log(error);
		}
	};

	const getItems = async () => {
		try {
			const response = await organizationalStructureService.getChart();
			const formattedData = transformTreeToArray(response);
			setItems(formattedData);
			await getUsuarios();
			await getDepartamentos();
			await getAtasMigrations();
			await getPlanoAcaoMigrations();
			await getIndicadoresMigrations();
		} catch (error) {
			console.log(error);
		}
	};

	const transformTreeToArray = (node, parentId = null) => {
		let result = [
			{
				id: node.id,
				name: node.name,
				title: node.title,
				color_name: node.color_name,
				color_responsible: node.color_responsible,
				font_family: node.font_family,
				is_support_area: node.is_support_area,
				parentId: parentId,
			},
		];

		if (node.children) {
			node.children.forEach((child) => {
				result = result.concat(transformTreeToArray(child, node.id));
			});
		}

		return result;
	};

	const getAll = async () => {
		setIsLoading(true);
		await getPermissions();
		if (temPermissaoVisualizar) {
			await getItems();
		}
		setIsLoading(false);
	};

	useEffect(() => {
		getAll();
	}, [temPermissaoVisualizar]);

	const getPermissions = async () => {
		try {
			const response = await permissionsService.getPermissions();
			for (let permission of response) {
				if (permission.role === "orgList") {
					setTemPermissaoVisualizar(true);
				}
			}
		} catch (error) {
			console.log(error);
		}
	};

	if (!temPermissaoVisualizar) return;

	function setModal() {
		setData({});
		setOpenCreateEditModal(true);
	}

	function setModalWithData(duplicatedData) {
		setData(duplicatedData);
		setOpenCreateEditModal(true);
	}

	function onCloseCreateEditModal() {
		setOpenCreateEditModal(false);
		setData({});
	}

	function updateData() {
		getAll();
	}

	const handleDuplicate = async (duplicatedData) => {
		setIsLoading(true);

		// Espera de 1 segundo para dar feedback visual ao usuário
		await new Promise((resolve) => setTimeout(resolve, 1000));

		setModalWithData(duplicatedData);
		setIsLoading(false);
	};

	const handleNodeClick = async (nodeData) => {
		const item = await getItem(nodeData.id);
		setData(item);
		setOpenCreateEditModal(true);
	};

	const handleStartSimulation = async () => {
		try {
			setIsLoading(true);
			const { simulation } =
				await organizationalStructureSimulationService.get();
			setSimulationItems(JSON.parse(JSON.stringify(simulation))); // Cria uma cópia profunda dos itens para a simulação
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoading(false);
		}
		setIsSimulation(true);
	};

	const handleStopSimulation = () => {
		setIsSimulation(false);
	};

	async function getItem(id) {
		try {
			const response = await organizationalStructureService.getById(id);
			return response;
		} catch (error) {
			console.log(error);
		}
	}

	const toggleTab = (tab) => {
		setActiveTab(tab);
	};

	// Funções de manipulação de alterações em selects das Atas
	const handleAtasDepartamentoChange = async (id, value) => {
		setAtasDepartamento((prev) => ({
			...prev,
			[id]: { organizational_structure_id: value },
		}));

		try {
			setAtasData((prev) =>
				prev.map((row) =>
					row.id === id
						? { ...row, organizational_structure_id: value }
						: row,
				),
			);
		} catch (error) {
			console.log(error);
		}
	};

	const handleAtasResponsavelChange = async (id, value) => {
		setAtasResponsavel((prev) => ({
			...prev,
			[id]: { responsible_id: value },
		}));

		try {
			setAtasData((prev) =>
				prev.map((row) =>
					row.id === id ? { ...row, responsible_id: value } : row,
				),
			);
		} catch (error) {
			console.log(error);
		}
	};

	// Funções de manipulação de alterações em selects dos Planos de Ação
	const handlePlanoAcaoDepartamentoChange = async (id, value) => {
		setPlanosDeAcaoDepartamento((prev) => ({
			...prev,
			[id]: { organizational_structure_id: value },
		}));

		try {
			setPlanosDeAcaoData((prev) =>
				prev.map((row) =>
					row.id === id
						? { ...row, organizational_structure_id: value }
						: row,
				),
			);
		} catch (error) {
			console.log(error);
		}
	};

	const handlePlanoAcaoResponsavelChange = async (id, value) => {
		setPlanosDeAcaoResponsavel((prev) => ({
			...prev,
			[id]: { responsible_id: value },
		}));

		try {
			setPlanosDeAcaoData((prev) =>
				prev.map((row) =>
					row.id === id ? { ...row, responsible_id: value } : row,
				),
			);
		} catch (error) {
			console.log(error);
		}
	};

	const handlePlanoAcaoAprovadorChange = async (id, value) => {
		setPlanosDeAcaoAprovador((prev) => ({
			...prev,
			[id]: { approving_id: value },
		}));

		try {
			setPlanosDeAcaoData((prev) =>
				prev.map((row) =>
					row.id === id ? { ...row, approving_id: value } : row,
				),
			);
		} catch (error) {
			console.log(error);
		}
	};

	// Funções de manipulação de alterações em selects dos Indicadores
	const handleIndicadoresDepartamentoChange = async (id, value) => {
		setIndicadoresDepartamento((prev) => ({
			...prev,
			[id]: { organization_id: value },
		}));

		try {
			setIndicadoresData((prev) =>
				prev.map((row) =>
					row.id === id ? { ...row, organization_id: value } : row,
				),
			);
		} catch (error) {
			console.log(error);
		}
	};

	// Função para salvar todas as alterações
	const handleSaveChanges = async () => {
		try {
			setIsLoading(true);

			// Salvar alterações das Atas
			for (const [id, value] of Object.entries(atasDepartamento)) {
				console.log("Enviando para API - Atas Departamento:", {
					id,
					data: {
						organizational_structure_id:
							value.organizational_structure_id,
					},
				});
				await ataEncaminhamentosService.put(id, {
					organizational_structure_id:
						value.organizational_structure_id,
				});
			}
			for (const [id, value] of Object.entries(atasResponsavel)) {
				console.log("Enviando para API - Atas Responsável:", {
					id,
					data: { responsible_id: value.responsible_id },
				});
				await ataEncaminhamentosService.put(id, {
					responsible_id: value.responsible_id,
				});
			}

			// Salvar alterações dos Planos de Ação
			for (const [id, value] of Object.entries(
				planosDeAcaoDepartamento,
			)) {
				console.log("Enviando para API - Plano Ação Departamento:", {
					id,
					data: {
						organizational_structure_id:
							value.organizational_structure_id,
					},
				});
				await actionPlansService.put(id, {
					organizational_structure_id:
						value.organizational_structure_id,
				});
			}
			for (const [id, value] of Object.entries(planosDeAcaoResponsavel)) {
				console.log("Enviando para API - Plano Ação Responsável:", {
					id,
					data: { responsible_id: value.responsible_id },
				});
				await actionPlansService.put(id, {
					responsible_id: value.responsible_id,
				});
			}
			for (const [id, value] of Object.entries(planosDeAcaoAprovador)) {
				console.log("Enviando para API - Plano Ação Aprovador:", {
					id,
					data: { approving_id: value.approving_id },
				});
				await actionPlansService.put(id, {
					approving_id: value.approving_id,
				});
			}

			// Salvar alterações dos Indicadores
			for (const [id, value] of Object.entries(indicadoresDepartamento)) {
				console.log("Enviando para API - Indicador Departamento:", {
					id,
					data: { organization_id: value.organization_id },
				});
				await indicadoresService.put(id, {
					organization_id: value.organization_id,
				});
			}

			// Limpar estados após salvar
			setAtasDepartamento({});
			setAtasResponsavel({});
			setPlanosDeAcaoDepartamento({});
			setPlanosDeAcaoResponsavel({});
			setPlanosDeAcaoAprovador({});
			setIndicadoresDepartamento({});

			// Recarregar dados
			await getAtasMigrations();
			await getPlanoAcaoMigrations();
			await getIndicadoresMigrations();

			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: "Alterações salvas com sucesso!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} catch (error) {
			console.error("Erro ao salvar alterações:", error);
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Erro",
				text: "Erro ao salvar alterações. Por favor, tente novamente.",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} finally {
			setIsLoading(false);
		}
	};

	// Função para verificar se há alterações pendentes
	const hasPendingChanges = () => {
		return (
			Object.keys(atasDepartamento).length > 0 ||
			Object.keys(atasResponsavel).length > 0 ||
			Object.keys(planosDeAcaoDepartamento).length > 0 ||
			Object.keys(planosDeAcaoResponsavel).length > 0 ||
			Object.keys(planosDeAcaoAprovador).length > 0 ||
			Object.keys(indicadoresDepartamento).length > 0
		);
	};

	const isActiveParent = (dep) => {
		// Se não tiver parent_id, é um departamento raiz e está ok
		if (!dep.parent_id) {
			return true;
		}

		let currentDep = dep;
		const visited = new Set(); // Conjunto para verificar ciclos

		// Verificar a cadeia de pais
		while (currentDep.parent_id) {
			// Verificar se já visitamos essa organização (evitar loop infinito)
			if (visited.has(currentDep.parent_id)) {
				return false; // Se encontrar um ciclo, retorna false
			}
			visited.add(currentDep.parent_id); // Marcar a organização como visitada

			// Verificar se o pai existe e está ativo
			const parentDep = departamentos.find(
				(d) => String(d.value) === String(currentDep.parent_id),
			);

			// Aqui está a correção - verificar se o pai existe e se está ativo (booleano)
			if (!parentDep || !parentDep.active) {
				return false; // Se o pai não existir ou estiver inativo, retorna false
			}

			currentDep = parentDep;
		}
		return true; // Todos os pais estão ativos
	};

	// Colunas para a tabela de Atas com selects
	const atasColumn = [
		{
			header: "ATA",
			accessorKey: "referrals",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps("firstColumn"),
			size: 30,
		},
		{
			header: "DEPARTAMENTO",
			accessorKey: "organizational_structure_id",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps(),
			size: 30,
			Cell: ({ row }) => {
				const id = row.original.id;
				const currentValue =
					atasDepartamento[id] ||
					row.original.organizational_structure_id ||
					"";

				// Encontrar o departamento atual na lista completa
				const currentDepartamento = departamentos.find(
					(dep) => String(dep.value) === String(currentValue),
				);

				// Filtrar departamentos para mostrar apenas os ativos ou o atual
				const filteredDepartamentos = departamentos.filter(
					(dep) =>
						String(dep.value) === String(currentValue) ||
						(dep.active && isActiveParent(dep)),
				);

				return (
					<Select
						value={currentDepartamento}
						onChange={(selectedOption) =>
							handleAtasDepartamentoChange(
								id,
								selectedOption.value,
							)
						}
						options={filteredDepartamentos}
						placeholder="Selecione..."
						isSearchable
						menuPortalTarget={document.body}
						styles={customStyles}
					/>
				);
			},
		},
		{
			header: "RESPONSÁVEL",
			accessorKey: "responsible_id",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps(),
			size: 30,
			Cell: ({ row }) => {
				const id = row.original.id;
				const currentValue =
					atasResponsavel[id] || row.original.responsible_id || "";

				return (
					<Select
						value={usuarios.find(
							(user) =>
								String(user.value) === String(currentValue),
						)}
						onChange={(selectedOption) =>
							handleAtasResponsavelChange(
								id,
								selectedOption ? selectedOption.value : null,
							)
						}
						options={usuarios}
						placeholder="Selecione..."
						isSearchable
						menuPortalTarget={document.body}
						styles={customStyles}
					/>
				);
			},
		},
	];

	// Colunas para a tabela de Planos de Ação com selects
	const planosDeAcaoColumn = [
		{
			header: "PLANO DE AÇÃO",
			accessorKey: "guideline",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps("firstColumn"),
			size: 30,
		},
		{
			header: "DEPARTAMENTO",
			accessorKey: "organizational_structure_id",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps(),
			size: 30,
			Cell: ({ row }) => {
				const id = row.original.id;
				const currentValue =
					atasDepartamento[id] ||
					row.original.organizational_structure_id ||
					"";

				// Encontrar o departamento atual na lista completa
				const currentDepartamento = departamentos.find(
					(dep) => String(dep.value) === String(currentValue),
				);

				// Filtrar departamentos para mostrar apenas os ativos ou o atual
				const filteredDepartamentos = departamentos.filter(
					(dep) =>
						String(dep.value) === String(currentValue) ||
						dep.active,
				);

				return (
					<Select
						value={currentDepartamento}
						onChange={(selectedOption) =>
							handlePlanoAcaoDepartamentoChange(
								id,
								selectedOption ? selectedOption.value : null,
							)
						}
						options={filteredDepartamentos}
						placeholder="Selecione..."
						isSearchable
						menuPortalTarget={document.body}
						styles={customStyles}
					/>
				);
			},
		},
		{
			header: "RESPONSÁVEL",
			accessorKey: "responsible_id",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps(),
			size: 30,
			Cell: ({ row }) => {
				const id = row.original.id;
				const currentValue =
					planosDeAcaoResponsavel[id] ||
					row.original.responsible_id ||
					"";

				return (
					<Select
						value={usuarios.find(
							(user) =>
								String(user.value) === String(currentValue),
						)}
						onChange={(selectedOption) =>
							handlePlanoAcaoResponsavelChange(
								id,
								selectedOption ? selectedOption.value : null,
							)
						}
						options={usuarios}
						placeholder="Selecione..."
						isSearchable
						menuPortalTarget={document.body}
						styles={customStyles}
					/>
				);
			},
		},
		{
			header: "APROVADOR",
			accessorKey: "approving_id",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps(),
			size: 30,
			Cell: ({ row }) => {
				const id = row.original.id;
				const currentValue =
					planosDeAcaoAprovador[id] ||
					row.original.approving_id ||
					"";

				return (
					<Select
						value={usuarios.find(
							(user) =>
								String(user.value) === String(currentValue),
						)}
						onChange={(selectedOption) =>
							handlePlanoAcaoAprovadorChange(
								id,
								selectedOption ? selectedOption.value : null,
							)
						}
						options={usuarios}
						placeholder="Selecione..."
						isSearchable
						menuPortalTarget={document.body}
						styles={customStyles}
					/>
				);
			},
		},
	];

	// Colunas para a tabela de Indicadores com selects
	const indicadoresColumn = [
		{
			header: "INDICADOR",
			accessorKey: "intention_goals",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps("firstColumn"),
			size: 30,
		},
		{
			header: "DEPARTAMENTO",
			accessorKey: "organization_id",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps(),
			size: 30,
			Cell: ({ row }) => {
				const id = row.original.id;
				const currentValue =
					indicadoresDepartamento[id] ||
					row.original.organization_id ||
					"";

				// Encontrar o departamento atual na lista completa
				const currentDepartamento = departamentos.find(
					(dep) => String(dep.value) === String(currentValue),
				);

				// Filtrar departamentos para mostrar apenas os ativos ou o atual
				const filteredDepartamentos = departamentos.filter(
					(dep) =>
						String(dep.value) === String(currentValue) ||
						dep.active,
				);

				return (
					<Select
						value={currentDepartamento}
						onChange={(selectedOption) =>
							handleIndicadoresDepartamentoChange(
								id,
								selectedOption ? selectedOption.value : null,
							)
						}
						options={filteredDepartamentos}
						placeholder="Selecione..."
						isSearchable
						menuPortalTarget={document.body}
						styles={customStyles}
					/>
				);
			},
		},
	];

	return (
		<React.Fragment>
			<Loading isLoading={isLoading}>
				<div className="page-content">
					<div className="container-fluid">
						<div
							className="card"
							style={{
								boxShadow:
									isSimulation && activeTab === "organograma"
										? "0 0 20px #f1b44c"
										: "none",
								transition: "box-shadow 0.3s ease",
							}}
						>
							<Nav tabs>
								<NavItem>
									<NavLink
										className={classnames({
											active: activeTab === "organograma",
										})}
										onClick={() => {
											toggleTab("organograma");
										}}
										style={{
											cursor: "pointer",
											color:
												activeTab === "organograma"
													? "#2a3042"
													: "inherit",
										}}
										onMouseEnter={(e) =>
											(e.target.style.color = "#2a3042")
										}
										onMouseLeave={(e) =>
											(e.target.style.color =
												activeTab === "organograma"
													? "#2a3042"
													: "inherit")
										}
									>
										Organograma
									</NavLink>
								</NavItem>
								<NavItem>
									<NavLink
										className={classnames({
											active: activeTab === "migracoes",
										})}
										onClick={() => {
											toggleTab("migracoes");
										}}
										style={{
											cursor: "pointer",
											color:
												activeTab === "migracoes"
													? "#2a3042"
													: "inherit",
										}}
										onMouseEnter={(e) =>
											(e.target.style.color = "#2a3042")
										}
										onMouseLeave={(e) =>
											(e.target.style.color =
												activeTab === "migracoes"
													? "#2a3042"
													: "inherit")
										}
									>
										Migrações
									</NavLink>
								</NavItem>
							</Nav>

							<TabContent activeTab={activeTab}>
								<TabPane tabId="organograma">
									{!isSimulation ? (
										<>
											<Col
												style={{
													display: "flex",
													alignItems: "center", // Alinha os itens verticalmente ao centro
													justifyContent:
														"space-between", // Espaço entre o título e os botões
													width: "100%", // Garante que o Col ocupe toda a largura disponível
													padding:
														"20px 20px 10px 20px", // Aplica padding apenas no topo e nas laterais
													position: "relative", // Necessário para o z-index funcionar
													zIndex: 1, // Garante que o título e os botões fiquem acima do OrgChartComponent
													backgroundColor: "white", // Fundo branco para evitar transparência
												}}
											>
												{/* Título */}
												<h4
													style={{
														fontSize: "18px",
														fontWeight: "bold",
														margin: 0, // Remove margens padrão do h4
													}}
												>
													Estrutura Organizacional
												</h4>

												{/* Botões */}
												<div
													style={{
														display: "flex",
														gap: "10px",
													}}
												>
													<Button
														type="button"
														style={{
															background: "none",
															color: "black",
															display: "flex",
															alignItems:
																"center",
															justifyContent:
																"center",
															gap: "2px",
															margin: 0, // Remove margens padrão do botão
														}}
														onClick={() =>
															setModal()
														}
													>
														<Plus size={16} />{" "}
														Adicionar Departamento
													</Button>
													<Button
														style={{
															background: "none",
															color: "black",
															display: "flex",
															alignItems:
																"center",
															justifyContent:
																"center",
															gap: "2px",
															margin: 0, // Remove margens padrão do botão
														}}
														onClick={() =>
															handleExportPdf()
														}
													>
														<FilePdf size={16} />{" "}
														Exportar PDF
													</Button>
													<Button
														style={{
															background: "none",
															color: "black",
															display: "flex",
															alignItems:
																"center",
															justifyContent:
																"center",
															gap: "2px",
															margin: 0, // Remove margens padrão do botão
														}}
														onClick={() =>
															handleExportWord()
														}
													>
														<FileDoc size={16} />{" "}
														Exportar Word
													</Button>
													<Button
														style={{
															background: "none",
															color: "black",
															display: "flex",
															alignItems:
																"center",
															justifyContent:
																"center",
															gap: "2px",
															margin: 0, // Remove margens padrão do botão
														}}
														onClick={
															handleStartSimulation
														}
													>
														<Pencil size={16} />{" "}
														Simular Estrutura
													</Button>
												</div>
											</Col>

											{/* Container do OrgChartComponent com overflow controlado */}
											<div
												style={{
													overflow: "auto",
													position: "relative",
													zIndex: 0,
												}}
											>
												<OrgChartComponent
													key={itemsKey}
													data={items}
													onNodeClick={
														handleNodeClick
													}
													updateData={updateData}
													ref={orgChartRef}
												/>
											</div>
										</>
									) : (
										<>
											<Col
												style={{
													display: "flex",
													alignItems: "center", // Alinha os itens verticalmente ao centro
													justifyContent:
														"space-between", // Espaço entre o título e os botões
													width: "100%", // Garante que o Col ocupe toda a largura disponível
													padding:
														"20px 20px 10px 20px", // Aplica padding apenas no topo e nas laterais
													position: "relative", // Necessário para o z-index funcionar
													zIndex: 1, // Garante que o título e os botões fiquem acima do OrgChartComponent
													backgroundColor: "white", // Fundo branco para evitar transparência
												}}
											>
												{/* Título */}
												<h4
													style={{
														fontSize: "18px",
														fontWeight: "bold",
														margin: 0, // Remove margens padrão do h4
													}}
												>
													Simulação de Estrutura
													Organizacional
												</h4>

												{/* Botões */}
												<div
													style={{
														display: "flex",
														gap: "10px",
													}}
												>
													<Button
														style={{
															background: "none",
															color: "black",
															display: "flex",
															alignItems:
																"center",
															justifyContent:
																"center",
															gap: "2px",
															margin: 0, // Remove margens padrão do botão
														}}
														onClick={() =>
															handleExportPdf()
														}
													>
														<FilePdf size={16} />{" "}
														Exportar PDF
													</Button>
													<Button
														style={{
															background: "none",
															color: "black",
															display: "flex",
															alignItems:
																"center",
															justifyContent:
																"center",
															gap: "2px",
															margin: 0, // Remove margens padrão do botão
														}}
														onClick={() =>
															handleExportWord()
														}
													>
														<FileDoc size={16} />{" "}
														Exportar Word
													</Button>
													<Button
														style={{
															background: "none",
															color: "black",
															display: "flex",
															alignItems:
																"center",
															justifyContent:
																"center",
															gap: "2px",
															margin: 0, // Remove margens padrão do botão
														}}
														onClick={
															handleStopSimulation
														}
													>
														<Pencil size={16} />{" "}
														Finalizar Simulação
													</Button>
												</div>
											</Col>

											{/* Container do OrgChartComponent com overflow controlado */}
											<div
												style={{
													overflow: "auto",
													position: "relative",
													zIndex: 0,
												}}
											>
												<OrgChartSimulation
													key={itemsKey}
													data={simulationItems}
													ref={orgChartSimulationRef}
												/>
											</div>
										</>
									)}
								</TabPane>
								<TabPane tabId="migracoes">
									<Col
										style={{
											padding: "20px",
										}}
									>
										{atasData && atasData.length > 0 && (
											<div
												style={{ marginBottom: "30px" }}
											>
												<h3>Atas</h3>
												<MaterialTableContainerWithNoFilters
													columns={atasColumn}
													data={atasData}
												/>
											</div>
										)}

										{planosDeAcaoData &&
											planosDeAcaoData.length > 0 && (
												<div
													style={{
														marginBottom: "30px",
													}}
												>
													<h3>Planos de Ação</h3>
													<MaterialTableContainerWithNoFilters
														columns={
															planosDeAcaoColumn
														}
														data={planosDeAcaoData}
													/>
												</div>
											)}

										{indicadoresData &&
											indicadoresData.length > 0 && (
												<div
													style={{
														marginBottom: "30px",
													}}
												>
													<h3>Indicadores</h3>
													<MaterialTableContainerWithNoFilters
														columns={
															indicadoresColumn
														}
														data={indicadoresData}
													/>
												</div>
											)}

										{(!atasData || atasData.length === 0) &&
											(!planosDeAcaoData ||
												planosDeAcaoData.length ===
													0) &&
											(!indicadoresData ||
												indicadoresData.length ===
													0) && (
												<h2
													style={{
														textAlign: "center",
														marginTop: "50px",
													}}
												>
													Não existe nenhuma migração
													a ser feita.
												</h2>
											)}

										{hasPendingChanges() && (
											<div
												style={{
													display: "flex",
													justifyContent: "start",
													marginTop: 20,
												}}
											>
												<Button
													onClick={handleSaveChanges}
													style={{
														width: 200,
														background: "none",
														padding: 10,
														cursor: "pointer",
														color: "black",
														display: "flex",
														alignItems: "center",
														justifyContent:
															"center",
														gap: 3,
													}}
													disabled={isLoading}
												>
													<FloppyDisk size={16} />{" "}
													Salvar Mudanças
												</Button>
											</div>
										)}
									</Col>
								</TabPane>
							</TabContent>
						</div>
					</div>
				</div>
				<ModalCreateEditItem
					show={openCreateEditModal}
					onCloseClick={onCloseCreateEditModal}
					data={data}
					updateData={updateData}
					onDuplicate={handleDuplicate}
				/>
			</Loading>
		</React.Fragment>
	);
};

export default withTranslation()(EstruturaOrgnizacional);
